import { getFormErrorMessage } from "@core/utility/get-error-message";
import { PackageActivityFormRule, PackageActivityFormSchema } from "@factory/form/form.rule";
import { useFormDefaultPackageActivity } from "@factory/form/form-default-package-activity.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponsePackageActivityModel } from "@factory/model/query-model-package-activity";
import { 
  useQyDeletePackageActivity, 
  useQyGetPackageActivityById, 
  useQyUpdatePackageActivity} from "@factory/query/package-activity.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditPackageActivity() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultPackageActivity();
  const navigate = useNavigate();
  const { mapUpdatePackageActivity, mapDeletePackageActivity } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { packageActivityId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID PackageActivity
  const handleGetApiSuccess = (data: GetResponsePackageActivityModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("package", responseData?.package || "");
      setValue("activity", responseData?.activity || "");
  };
  const {
    data: packageActivityResponse,
    isLoading,
    isError: packageActivityError,
  } = useQyGetPackageActivityById(packageActivityId || "", handleGetApiSuccess);
  const packageActivityData = packageActivityResponse?.data?.[0];

  // API UPDATE PackageActivity
  const handleUpdateApiSuccess = () => {
    showSuccess("PackageActivity updated successfully");
  };
  const { mutate: updatePackageActivity, isLoading: isEditLoading } = useQyUpdatePackageActivity(
    handleUpdateApiSuccess
  );

  // API DELETE PackageActivity
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("PackageActivity deleted successfully");
  };
  const { mutate: deletePackageActivity } = useQyDeletePackageActivity(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<PackageActivityFormSchema>({
    defaultValues: getDefault(packageActivityData),
    resolver: zodResolver(PackageActivityFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: PackageActivityFormSchema) => {
    if (!packageActivityData || !packageActivityData.code) {
      throw new Error("No packageActivity data");
    }

    const formData = mapUpdatePackageActivity(form, packageActivityData.code);
    updatePackageActivity(formData);
  };
  const handleValidateError = (err: FieldErrors<PackageActivityFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!packageActivityData || !packageActivityData.code) {
      throw new Error("No packageActivity data found");
    }

    const form = mapDeletePackageActivity(packageActivityData.code);
    deletePackageActivity(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    packageActivityId,
    packageActivityData,
    isLoading,
    packageActivityError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}